import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import styles from '../styles/Events.module.scss';
import { categoryIcons } from '../assets/icons/categoryIcons';
import { triggerHaptic, hapticImpact } from '../utils/haptics';
import UniversalModal from '../components/UniversalModal/UniversalModal';

const EVENT_COLORS = [
  '#ef4444',
  '#f97316',
  '#eab308',
  '#22c55e',
  '#06b6d4',
  '#3b82f6',
  '#a855f7',
  '#ec4899',
];

const calculateDaysLeft = (targetDate) => {
  const target = new Date(targetDate);
  target.setHours(0, 0, 0, 0);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const diff = target - today;
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
};

const formatDateShort = (dateInput) => {
  if (!dateInput) return '';

  const d = new Date(dateInput);

  if (isNaN(d.getTime())) return '';

  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');

  return `${day}.${month}`;
};

const parseDateInput = (text) => {
  const now = new Date();
  const currentYear = now.getFullYear();

  const parts = text.split('.');
  const day = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10);

  if (!day || !month || month > 12 || day > 31) return null;

  let targetDate = new Date(currentYear, month - 1, day);

  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  if (targetDate < today) {
    targetDate.setFullYear(currentYear + 1);
  }

  const y = targetDate.getFullYear();
  const m = String(targetDate.getMonth() + 1).padStart(2, '0');
  const d = String(targetDate.getDate()).padStart(2, '0');

  return `${y}-${m}-${d}`;
};

export default function Events() {
  const [events, setEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [name, setName] = useState('');
  const [date, setDate] = useState('');
  const [selectedIcon, setSelectedIcon] = useState('Star');
  const [selectedColor, setSelectedColor] = useState(EVENT_COLORS[5]);
  const [isSaving, setIsSaving] = useState(false);

  const iconKeys = useMemo(
    () => Object.keys(categoryIcons || {}).slice(0, 20),
    []
  );

  const fetchEvents = async () => {
    try {
      const res = await fetch('/api/events');
      if (res.ok) {
        const data = await res.json();
        setEvents(data);
      }
    } catch (error) {
      console.error('Ошибка загрузки событий', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents();
  }, []);

  const sortedEvents = useMemo(() => {
    return [...events].sort((a, b) => {
      return calculateDaysLeft(a.date) - calculateDaysLeft(b.date);
    });
  }, [events]);

  const handleDelete = async (id) => {
    triggerHaptic(hapticImpact.medium);
    try {
      const res = await fetch(`/api/events/${id}`, { method: 'DELETE' });
      if (res.ok) {
        setEvents((prev) => prev.filter((e) => e.id !== id));
      }
    } catch (error) {
      console.error('Ошибка удаления', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const dbDate = parseDateInput(date);

    if (!name.trim() || !dbDate) {
      alert('Введите дату в формате ДД.ММ');
      return;
    }

    setIsSaving(true);
    triggerHaptic(hapticImpact.light);

    const payload = {
      name: name.trim(),
      date: dbDate,
      icon: selectedIcon,
      color: selectedColor,
    };
    try {
      const res = await fetch('/api/events', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        const newEvent = await res.json();
        setEvents((prev) => [...prev, newEvent]);
        setIsModalOpen(false);
        setName('');
        setDate('');
        triggerHaptic(hapticImpact.success);
      }
    } catch (error) {
      console.error('Ошибка сохранения', error);
      triggerHaptic(hapticImpact.error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className={styles.events}>
      <header className={styles.events__header}>
        <h1 className={styles.events__title}>События</h1>
        <button
          className={styles.events__addButton}
          onClick={() => setIsModalOpen(true)}>
          + Добавить
        </button>
      </header>

      {isLoading ? (
        <div className={styles.events__loader}>Загрузка...</div>
      ) : sortedEvents.length > 0 ? (
        <div className={styles.events__grid}>
          <AnimatePresence>
            {sortedEvents.map((event) => {
              const daysLeft = calculateDaysLeft(event.date);
              const Icon = categoryIcons[event.icon] || categoryIcons.Star;
              const isUrgent = daysLeft >= 0 && daysLeft <= 7;

              let urgencyClass = '';
              if (daysLeft >= 0 && daysLeft <= 7) {
                urgencyClass = styles.events__card_urgent;
              } else if (daysLeft > 7 && daysLeft <= 30) {
                urgencyClass = styles.events__card_soon;
              }

              return (
                <motion.div
                  key={event.id}
                  layout
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  className={[styles.events__card, urgencyClass]
                    .filter(Boolean)
                    .join(' ')}
                  style={{ '--accent-color': event.color }}>
                  <div className={styles.events__cardIcon}>
                    {Icon && <Icon />}
                  </div>

                  <div className={styles.events__cardInfo}>
                    <p className={styles.events__cardTitle}>{event.name}</p>
                    <h3 className={styles.events__cardDays}>
                      {daysLeft}
                      {isUrgent && (
                        <span className={styles.events__fire}>🔥</span>
                      )}
                    </h3>
                    <p className={styles.events__cardSubtitle}>
                      Дней до: {formatDateShort(event.date)}
                    </p>
                  </div>

                  <button
                    className={styles.events__cardDelete}
                    onClick={() => handleDelete(event.id)}
                  />
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>
      ) : (
        <div className={styles.events__empty}>
          <p className={styles.events__emptyText}>
            У вас пока нет активных событий
          </p>
          <button
            className={styles.events__emptyButton}
            onClick={() => setIsModalOpen(true)}>
            Создать событие
          </button>
        </div>
      )}

      <UniversalModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}>
        <form className={styles.form} onSubmit={handleSubmit}>
          <h2 className={styles.form__title}>Новое событие</h2>

          <label className={styles.form__label}>
            <span className={styles.form__labelText}>Название</span>
            <input
              type='text'
              className={styles.form__input}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder='Например: Отпуск'
              required
            />
          </label>

          <label className={styles.form__label}>
            <span className={styles.form__labelText}>Дата (ДД.ММ)</span>
            <input
              type='text'
              inputMode='decimal'
              className={styles.form__input}
              value={date}
              onChange={(e) => {
                let v = e.target.value.replace(/[^\d.]/g, '');
                if (v.length === 2 && !v.includes('.')) v += '.';
                if (v.length > 5) v = v.slice(0, 5);
                setDate(v);
              }}
              placeholder='Пример: 27.01'
              required
            />
          </label>

          <div className={styles.form__section}>
            <span className={styles.form__labelText}>Иконка</span>
            <div className={styles.form__icons}>
              {iconKeys.map((iconKey) => {
                const IconComponent = categoryIcons[iconKey];
                return (
                  <button
                    key={iconKey}
                    type='button'
                    className={[
                      styles.form__iconBtn,
                      selectedIcon === iconKey
                        ? styles.form__iconBtn_active
                        : '',
                    ].join(' ')}
                    onClick={() => setSelectedIcon(iconKey)}>
                    <IconComponent />
                  </button>
                );
              })}
            </div>
          </div>

          <div className={styles.form__section}>
            <span className={styles.form__labelText}>Цвет</span>
            <div className={styles.form__colors}>
              {EVENT_COLORS.map((hex) => (
                <button
                  key={hex}
                  type='button'
                  className={[
                    styles.form__colorBtn,
                    selectedColor === hex ? styles.form__colorBtn_active : '',
                  ].join(' ')}
                  style={{ backgroundColor: hex }}
                  onClick={() => setSelectedColor(hex)}
                />
              ))}
            </div>
          </div>

          <button
            type='submit'
            className={styles.form__submit}
            disabled={!name || !date || isSaving}>
            {isSaving ? 'Сохранение...' : 'Создать'}
          </button>
        </form>
      </UniversalModal>
    </div>
  );
}
